import { Box, Checkbox, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import DropdownTrigger, { BORDER, TEXT_MUTED } from 'Components/FilterDropdownTrigger';

export interface MultiSelectOptionI {
  id: string;
  name: string;
}

interface Props {
  icon: ReactNode;
  label: string;
  options: MultiSelectOptionI[];
  /** Ids seleccionados. Arreglo vacío = "todos". */
  value: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
  /** Cuando se define, el menú incluye un buscador interno. */
  searchPlaceholder?: string;
  emptyLabel: string;
  /** Sufijo del resumen cuando hay 2+ seleccionados, ej. "puestos". */
  selectedNoun: string;
}

const styles = {
  searchWrap: {
    px: 1.5,
    py: 1,
  },
  searchInput: {
    border: `1px solid ${BORDER}`,
    borderRadius: '6px',
    padding: '7px 10px',
    fontSize: 13,
    width: '100%',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
  item: {
    fontSize: 13,
    py: 0.5,
  },
  empty: {
    px: 2,
    py: 1.5,
    fontSize: 12.5,
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
};

/**
 * Dropdown de selección múltiple con buscador opcional.
 *
 * Vive dentro del reporte (no en `Components/`) a propósito: es la primera vez
 * que el proyecto necesita este patrón y no queremos introducir un componente
 * compartido nuevo en el mismo cambio que estrena el reporte. Si otra pantalla
 * lo necesita después, se promueve entonces.
 *
 * Convención: `value` vacío significa "todos", igual que los filtros de un solo
 * valor del Reporte de Sesiones cuando están sin seleccionar.
 */
export default function MultiSelectFilter({
  icon,
  label,
  options,
  value,
  onChange,
  allLabel,
  searchPlaceholder,
  emptyLabel,
  selectedNoun,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');

  const visibleOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter(option => option.name.toLowerCase().includes(term));
  }, [options, search]);

  const summary = useMemo(() => {
    if (value.length === 0) return allLabel;
    if (value.length === 1) return options.find(option => option.id === value[0])?.name ?? allLabel;
    return `${value.length} ${selectedNoun}`;
  }, [value, options, allLabel, selectedNoun]);

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter(current => current !== id) : [...value, id]);
  };

  const close = () => {
    setAnchor(null);
    setSearch('');
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <DropdownTrigger icon={icon} label={label} value={summary} active={value.length > 0} onClick={setAnchor} />
      <Menu anchorEl={anchor} open={!!anchor} onClose={close} PaperProps={{ sx: { maxHeight: 360, minWidth: 260 } }}>
        {searchPlaceholder && (
          <Box sx={styles.searchWrap} onKeyDown={event => event.stopPropagation()}>
            <input
              autoFocus
              value={search}
              placeholder={searchPlaceholder}
              onChange={event => setSearch(event.target.value)}
              style={styles.searchInput}
            />
          </Box>
        )}

        <MenuItem sx={styles.item} selected={value.length === 0} onClick={() => onChange([])}>
          {allLabel}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />

        {visibleOptions.map(option => (
          <MenuItem key={option.id} sx={styles.item} onClick={() => toggle(option.id)}>
            <Checkbox size="small" checked={value.includes(option.id)} sx={{ mr: 1, p: 0.5 }} />
            {option.name}
          </MenuItem>
        ))}

        {visibleOptions.length === 0 && <Typography sx={styles.empty}>{emptyLabel}</Typography>}
      </Menu>
    </Box>
  );
}
